import React, { FormEvent, useState, useMemo } from 'react';
import { SearchResults } from 'types/search';
import useStyles from './styles';
import { SearchQueryVariables } from '.';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import InputBase from '@mui/material/InputBase';
import InputAdornment from '@mui/material/InputAdornment';
import { PostGrid, SearchResultUserList } from 'organisms';
import * as IconMUI from '@mui/icons-material';
import clsx from 'clsx';
import { ROUTES } from 'config/Nav';
import { useRouter } from 'next/router';
import { reportError } from 'lib/errors';
import { ScrollToTop, QueryResult } from '@elevator/ui';
import { SectionHeader } from 'molecules';
import { LoadingError } from 'atoms';

/** `id`s for `Tab` buttons — paired with {@link SEARCH_PANEL_IDS} for `aria-controls` / `aria-labelledby`. */
const SEARCH_TAB_IDS = [
  'search-tab-all',
  'search-tab-posts',
  'search-tab-users',
] as const;
/** `id`s for the single visible `role="tabpanel"` region (index matches `activeTab`). */
const SEARCH_PANEL_IDS = [
  'search-tabpanel-all',
  'search-tabpanel-posts',
  'search-tabpanel-users',
] as const;

/**
 * Search Component
 *
 * Displays search results with tabs for filtering (All, Posts, Users).
 * Handles search input, pagination, and result display.
 *
 * Features:
 * - Root `<main>` with `data-layout="search"`, vertical `gap` between blocks
 * - Search input with clear button
 * - Tab-based filtering (All, Posts, Users): `tab`/`tabpanel` wiring; visible `h2` section titles only on **All** (and when tabs are omitted)
 * - Post pagination with load more
 * - User list display
 * - Empty state handling
 *
 * Used in:
 * - Search layout index (rendered for /search page)
 *
 * @param props - Component props
 * @param props.searchResults - Search results data (posts and users)
 * @param props.searchParam - Current search query string
 * @param props.loading - Whether initial data is loading
 * @param props.loadingMore - Whether more posts are being fetched
 * @param props.error - GraphQL query error
 * @param props.fetchMore - Function to fetch more posts
 * @param props.variables - GraphQL query variables
 * @returns Search component with results and filters
 */
const Search = (props: SearchPropTypes): React.JSX.Element => {
  const { classes } = useStyles();
  const { searchParam, loading } = props;
  const { error, searchResults, fetchMore, variables, loadingMore } = props;

  const posts = useMemo(
    () =>
      searchResults && searchResults.publishedPosts ? searchResults.publishedPosts : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchResults?.publishedPosts],
  );
  const users = useMemo(
    () => (searchResults && searchResults.searchUsers ? searchResults.searchUsers : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchResults?.searchUsers],
  );

  const [activeTab, setActiveTab] = useState(0);
  const [searchInputValue, setSearchInputValue] = useState(searchParam || '');
  // Display limit state - resets when searchParam changes (adjust during render)
  const [displayLimit, setDisplayLimit] = useState(variables.limit);
  const [prevSearchParam, setPrevSearchParam] = useState(searchParam);
  const [revealAfterLength, setRevealAfterLength] = useState<number | null>(null);
  if (searchParam !== prevSearchParam) {
    setPrevSearchParam(searchParam);
    setDisplayLimit(variables.limit);
    setRevealAfterLength(null);
  }
  const router = useRouter();

  const onChangeTab = (event: React.ChangeEvent<{}>, newValue: number) => {
    setActiveTab(newValue);
  };

  // Slice posts array based on displayLimit
  const allPosts = posts;
  const displayedPosts = useMemo(
    () => allPosts.slice(0, displayLimit),
    [allPosts, displayLimit],
  );

  // After fetchMore, reveal the next page once cache length grows
  if (revealAfterLength != null && allPosts.length > revealAfterLength) {
    setRevealAfterLength(null);
    setDisplayLimit((prev) => prev + variables.limit);
  }

  const onLoadMore = () => {
    // Check if more posts exist in cache
    if (allPosts.length > displayLimit) {
      // More posts available in cache - just increase display limit (instant)
      setDisplayLimit((prev) => prev + variables.limit);
    } else {
      // Need to fetch more posts; reveal once cache grows
      setRevealAfterLength(allPosts.length);
      fetchMore({
        variables: {
          search: variables.search,
          limit: variables.limit,
          skip: allPosts.length,
        },
        updateQuery: (
          prev: { publishedPosts: SearchResults['publishedPosts'] },
          {
            fetchMoreResult,
          }: { fetchMoreResult?: { publishedPosts: SearchResults['publishedPosts'] } },
        ) => {
          if (!fetchMoreResult) return prev;

          const newPosts = [...prev.publishedPosts, ...fetchMoreResult.publishedPosts];

          return {
            ...prev,
            publishedPosts: newPosts,
          };
        },
      });
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    router.push({
      pathname: ROUTES.SEARCH.to,
      query: {
        s: searchInputValue.trim(),
      },
    });
  };

  const handleChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) =>
    setSearchInputValue(value);

  const handleClear = () => {
    setSearchInputValue('');
  };

  const renderSearchResultInput = () => {
    return (
      <Box
        component="form"
        autoComplete="off"
        onSubmit={handleSubmit}
        className={classes.inputStyling}
        width="100%"
      >
        <InputBase
          type="text"
          id="elevator-site-search"
          name="elevator-site-search"
          value={searchInputValue}
          onChange={handleChange}
          className={classes.inputStyling}
          inputProps={{
            className: classes.inputStyling,
            autoComplete: 'off',
            'aria-label': 'search',
          }}
          fullWidth
          startAdornment={
            <InputAdornment position="start">
              <IconMUI.Search className={classes.searchIcon} />
            </InputAdornment>
          }
          endAdornment={
            searchInputValue &&
            searchInputValue.length > 0 && (
              <InputAdornment position="end" onClick={handleClear}>
                <IconMUI.Clear fontSize="small" className={classes.clearIcon} />
              </InputAdornment>
            )
          }
        />
        <InputBase
          id="searchSubmit"
          type="submit"
          value="submit"
          className={classes.submitButton}
        />
      </Box>
    );
  };

  /**
   * NOTE: These could potentially be their own component, but since they are
   * single use-case at the time of this writing it will be localized to this layout
   */
  const renderTabFilters = () => {
    return (
      <Tabs
        value={activeTab}
        onChange={onChangeTab}
        aria-label="Search results"
        classes={{
          indicator: classes.tabIndicator,
        }}
        sx={{ width: '100%' }}
      >
        <Tab
          id={SEARCH_TAB_IDS[0]}
          aria-controls={SEARCH_PANEL_IDS[0]}
          label="All"
          classes={{
            root: classes.tabRoot,
          }}
        />
        <Tab
          id={SEARCH_TAB_IDS[1]}
          aria-controls={SEARCH_PANEL_IDS[1]}
          label="Posts"
          classes={{
            root: classes.tabRoot,
          }}
        />
        <Tab
          id={SEARCH_TAB_IDS[2]}
          aria-controls={SEARCH_PANEL_IDS[2]}
          label="Users"
          classes={{
            root: classes.tabRoot,
          }}
        />
      </Tabs>
    );
  };

  const renderAllTabView = () => {
    return (
      <Box className={classes.allSearchResultsContainer}>
        <Box className={classes.usersSearchResultsListContainer}>
          <SearchResultUserList title="Users" users={users} layout="grid" />
        </Box>

        <Box
          component="section"
          className={clsx(
            classes.postSearchResultsListContainer,
            classes.sectionContainer,
          )}
        >
          <SectionHeader
            title="Posts"
            titleVariant="h5"
            titleComponent="h2"
            divider={false}
          />
          <PostGrid
            posts={displayedPosts}
            loadLimit={variables.limit}
            lcpPriorityCount={variables.limit}
            onLoadMore={onLoadMore}
            loadingMore={loadingMore}
            isLoading={loading}
            error={error ?? null}
            variant="small"
            adUnitPrefix="ELEVATOR-Mid"
          />
        </Box>
      </Box>
    );
  };

  const renderPostTabView = (showSectionTitle: boolean) => {
    return (
      <Box className={classes.singleTabContainer}>
        <Box component="section" className={classes.sectionContainer}>
          {showSectionTitle ? (
            <SectionHeader
              title="Posts"
              titleVariant="h5"
              titleComponent="h2"
              divider={false}
            />
          ) : null}
          <PostGrid
            posts={displayedPosts}
            loadLimit={variables.limit}
            lcpPriorityCount={variables.limit}
            onLoadMore={onLoadMore}
            loadingMore={loadingMore}
            isLoading={loading}
            error={error ?? null}
            variant="small"
            adUnitPrefix="ELEVATOR-Mid"
          />
        </Box>
      </Box>
    );
  };

  const renderUserTabView = (showSectionTitle: boolean) => {
    return (
      <Box
        className={clsx(
          classes.singleTabContainer,
          classes.usersSearchResultsListContainer,
        )}
      >
        <SearchResultUserList
          title={showSectionTitle ? 'Users' : undefined}
          users={users}
          layout="grid"
        />
      </Box>
    );
  };

  const renderContent = () => {
    // If no search query, don't show any content (just the search bar)
    if (!searchParam) {
      return null;
    }

    // Don't show "no results" while loading - wait for data to arrive
    if (loading) {
      return null;
    }

    if (allPosts.length && users.length) {
      switch (activeTab) {
        case 0:
          return renderAllTabView();
        case 1:
          return renderPostTabView(false);
        case 2:
          return renderUserTabView(false);
        default:
          return null;
      }
    } else if (allPosts.length && !users.length) {
      return renderPostTabView(true);
    } else if (!allPosts.length && users.length) {
      return renderUserTabView(true);
    } else {
      return (
        <Typography
          variant="h4"
          color="textSecondary"
          align="center"
          className={classes.emptyResultsContainer}
          component="div"
        >
          No results found for &quot;{searchParam}&quot;
        </Typography>
      );
    }
  };

  const showResultTabs =
    !error && Boolean(searchParam) && allPosts.length > 0 && users.length > 0;

  return (
    <>
      <Box component="main" className={classes.container} data-layout="search">
        {renderSearchResultInput()}
        {error && (
          <Box py={2}>
            <LoadingError
              error={error}
              failedTarget="search results page"
              reportError={reportError}
            />
          </Box>
        )}
        {showResultTabs && renderTabFilters()}
        {!error && <Divider variant="fullWidth" />}
        {!error &&
          (showResultTabs ? (
            <Box
              role="tabpanel"
              id={SEARCH_PANEL_IDS[activeTab]}
              aria-labelledby={SEARCH_TAB_IDS[activeTab]}
            >
              {renderContent()}
            </Box>
          ) : (
            renderContent()
          ))}
      </Box>
      <ScrollToTop visibleYOffset={90} />
    </>
  );
};

export interface SearchPropTypes extends QueryResult<
  'searchResults',
  SearchResults,
  SearchQueryVariables
> {
  searchParam?: string;
}

export default Search;
